/** Number-of-the-day content, keyed by astro-today `content_id` ("N.M"). 90 entries. */
export interface NumberOfTheDay {
  name: string
  tags: string[]
  description: string
}

export const NUMBER_OF_THE_DAY: Record<string, NumberOfTheDay> = {
  "1.1": {
    name: "Day of new beginnings",
    tags: ["Initiative", "Courage", "Independence"],
    description:
      "Today opens a door. Whatever you start now sets a tone for the cycle ahead. Trust your instincts and move first — even if you don't yet have the full plan. Independence is a strength today, not a flaw. Lead, don't wait to be led.",
  },
  "1.2": {
    name: "Day of first moves",
    tags: ["Decision", "Action", "Self-trust"],
    description:
      "The moment to decide is now. Stop circling, stop polling friends. The first option that felt right probably is right. Make the call, send the message, take the step. Today rewards motion, not analysis.",
  },
  "1.3": {
    name: "Day of standing alone",
    tags: ["Solo", "Autonomy", "Conviction"],
    description:
      "You don't need permission today. The work that matters is yours alone. Step away from the group, the meeting, the noise. What you accomplish in solitude becomes the foundation others build on later. Trust your own counsel.",
  },
  "1.4": {
    name: "Day of bold openings",
    tags: ["Risk", "Vision", "Pioneer"],
    description:
      "Whatever you launch today travels far. Speak the idea you've been holding. Apply for the thing. Pitch the project. You'll never feel fully ready — start anyway. Boldness writes the rest.",
  },
  "1.5": {
    name: "Day of leading from the front",
    tags: ["Leadership", "Initiative", "Example"],
    description:
      "Others are watching today, whether you know it or not. Set the tone. Show up first, do the work that others will model. Leadership today isn't about giving orders — it's about being the first one in motion.",
  },
  "1.6": {
    name: "Day of starting fresh",
    tags: ["Renewal", "Beginning", "Clean slate"],
    description:
      "Old chapter closed. Today is the blank page. What's the one thing you'd start if no one knew your history? Begin there. Today rewards courage over consistency.",
  },
  "1.7": {
    name: "Day of single-pointed focus",
    tags: ["Focus", "Clarity", "Drive"],
    description:
      "Today wants you to choose. Not seven goals, not three — one. Pour everything into it. The energy of 1 is concentrated, sharp, singular. Distractions today will steal more than they're worth.",
  },
  "1.8": {
    name: "Day of trusting your gut",
    tags: ["Instinct", "Self-trust", "Conviction"],
    description:
      "The advice is coming from all sides. Listen, then go quiet. Your own knowing is louder than the noise — if you give it room. Today is not for committee decisions. It's for the one inside you who already knows.",
  },
  "1.9": {
    name: "Day of original thinking",
    tags: ["Originality", "Independence", "Innovation"],
    description:
      "Today rewards the path no one else is walking. Stop looking at how everyone else does it. The fresh idea is the right idea. Trust the version of you that isn't trying to fit in.",
  },
  "1.10": {
    name: "Day of stepping into your power",
    tags: ["Power", "Self", "Beginning"],
    description:
      "You've been waiting for permission you don't need. Today is the day you stop. Take the role, claim the title, ask for the salary, set the terms. The energy of 1 is the energy of becoming the leader of your own life.",
  },
  "2.1": {
    name: "Day of cooperation and care",
    tags: ["Partnership", "Patience", "Listening"],
    description:
      "Today asks you to soften. Slow down, listen carefully, and find your place beside others rather than in front of them. Diplomacy and patience are more useful than force. Look for what others are not saying — and respond with care.",
  },
  "2.2": {
    name: "Day of quiet partnership",
    tags: ["Collaboration", "Tact", "Harmony"],
    description:
      "The win today belongs to two, not one. Whoever is across from you — partner, colleague, friend — matters more than the goal. Make space for their thinking. Today, the best move is the move you make together.",
  },
  "2.3": {
    name: "Day of receiving",
    tags: ["Receptivity", "Trust", "Openness"],
    description:
      "You've been giving for a while. Today is for opening your hands the other way. Let someone help, compliment, contribute, lead. Receiving is not weakness; it's the other half of partnership. Practice it.",
  },
  "2.4": {
    name: "Day of careful listening",
    tags: ["Listening", "Presence", "Attention"],
    description:
      "Don't reply. Listen. The person in front of you has something to say that they haven't said yet — and today they're ready. Your patience creates the space they need. Whatever they reveal will matter.",
  },
  "2.5": {
    name: "Day of diplomacy",
    tags: ["Diplomacy", "Patience", "Grace"],
    description:
      "Conflict is in the air, but force is the wrong response. Slow down. Find the third option. Soften the edge of your words without losing your meaning. Today, the diplomat wins what the warrior cannot.",
  },
  "2.6": {
    name: "Day of soft strength",
    tags: ["Gentleness", "Strength", "Resolve"],
    description:
      "Strength today looks like patience. It looks like staying with someone, holding a position quietly, not needing to prove anything. The softer you are, the more weight you carry.",
  },
  "2.7": {
    name: "Day of seeing the other side",
    tags: ["Empathy", "Perspective", "Compassion"],
    description:
      "Step into someone else's view today. Whatever you've been arguing for — pause it, and ask what the other side is protecting. You'll see something you missed. Today rewards understanding over winning.",
  },
  "2.8": {
    name: "Day of mending",
    tags: ["Repair", "Reconciliation", "Care"],
    description:
      "A small rupture has been waiting. Today is the day to reach across. A short message, an acknowledgment, an apology — something simple. The other person will receive it more openly than you expect.",
  },
  "2.9": {
    name: "Day of slow pace",
    tags: ["Patience", "Slowness", "Rest"],
    description:
      "The push will create resistance today. Slow your pace deliberately. Walk where you'd usually rush, breathe where you'd usually answer, wait where you'd usually press. The day yields what it withholds from the impatient.",
  },
  "2.10": {
    name: "Day of harmony at home",
    tags: ["Home", "Comfort", "Connection"],
    description:
      "The most important work today is closest to you. Time with the person you live with, the friend who's been quiet, the family member you've meant to call. Today asks for nearness, not ambition.",
  },
  "3.1": {
    name: "Day of expression and joy",
    tags: ["Creativity", "Expression", "Lightness"],
    description:
      "Let yourself be visible. Today rewards play, conversation, and creative risk. Share what you've been holding back — a story, an idea, a feeling. Don't filter too much. The day's energy carries lightness; ride it.",
  },
  "3.2": {
    name: "Day of making something",
    tags: ["Creation", "Craft", "Play"],
    description:
      "The hands want to work today. Build something — a meal, a sketch, a piece of writing, a song you sing badly. The point is the making, not the product. Today asks for unselfconscious creation.",
  },
  "3.3": {
    name: "Day of conversation",
    tags: ["Conversation", "Connection", "Words"],
    description:
      "Reach out. The friend you haven't called, the colleague you've avoided, the stranger sitting next to you. Words flow easily today — let them. One real conversation can rearrange your week.",
  },
  "3.4": {
    name: "Day of unfiltered honesty",
    tags: ["Honesty", "Voice", "Expression"],
    description:
      "The thing you've been editing in your head — say it out loud. Lightly, not heavily. Today supports honest words spoken with warmth. People will hear them differently than you fear.",
  },
  "3.5": {
    name: "Day of small joy",
    tags: ["Joy", "Lightness", "Pleasure"],
    description:
      "Find the small joy and stay with it. The first sip of coffee, the song that makes you move, the joke that breaks open the room. Today rewards noticing what's already good. Don't rush past it.",
  },
  "3.6": {
    name: "Day of creative risk",
    tags: ["Risk", "Creativity", "Boldness"],
    description:
      "Show the unfinished thing. Pitch the half-formed idea. Wear the outfit you've been saving. Today, perfection is the enemy of presence. Share before you're ready.",
  },
  "3.7": {
    name: "Day of social warmth",
    tags: ["Connection", "Warmth", "Belonging"],
    description:
      "People matter today. Skip the productivity, lean into the human. Lunch with a friend, drinks with strangers, a long phone call. Today's gold is in the room with other people.",
  },
  "3.8": {
    name: "Day of playful experimentation",
    tags: ["Play", "Experiment", "Curiosity"],
    description:
      "Treat today like a sandbox. Try the new recipe, take the unfamiliar route, ask the weird question. The energy of 3 doesn't want to be efficient — it wants to be alive. Let it.",
  },
  "3.9": {
    name: "Day of telling your story",
    tags: ["Story", "Voice", "Self-expression"],
    description:
      "Someone needs to hear what you've lived. Don't perform it — just tell it. Your story, told plainly, is more useful than advice. Today is for being known, not impressive.",
  },
  "3.10": {
    name: "Day of color and movement",
    tags: ["Vibrance", "Movement", "Aliveness"],
    description:
      "The day wants energy. Move your body, change your environment, add color to your space. Static today is heavy. Whatever's stuck — shake it. The shift unlocks everything.",
  },
  "4.1": {
    name: "Day of work and foundation",
    tags: ["Discipline", "Structure", "Focus"],
    description:
      "Today is for steady building. Choose one important task and finish it. Avoid shortcuts; they cost more later. Routine, focus, and patience are your tools. The progress will feel small but it lasts.",
  },
  "4.2": {
    name: "Day of finishing one thing",
    tags: ["Completion", "Focus", "Discipline"],
    description:
      "Don't add another item to the list. Pick the thing that's been hanging over you and close it. The relief alone is worth the effort. Today rewards completion over starting.",
  },
  "4.3": {
    name: "Day of cleaning the foundation",
    tags: ["Order", "Foundation", "Reset"],
    description:
      "Organize the chaos. Inbox, desk, files, money — whichever is loudest. Today wants you in maintenance mode, not in expansion mode. Tomorrow will go further on a clean base.",
  },
  "4.4": {
    name: "Day of discipline over inspiration",
    tags: ["Discipline", "Consistency", "Patience"],
    description:
      "The mood isn't going to come. Do the work anyway. Today the people who win are the ones who show up regardless of feeling. Inspiration is a follower, not a leader.",
  },
  "4.5": {
    name: "Day of slow, steady progress",
    tags: ["Patience", "Steadiness", "Endurance"],
    description:
      "Big moves today will backfire. Small consistent moves will compound. Choose the unglamorous task, the boring habit, the quiet revision. Today rewards the tortoise.",
  },
  "4.6": {
    name: "Day of practical money",
    tags: ["Money", "Planning", "Order"],
    description:
      "The numbers want attention. Look at the budget, check the accounts, review the subscription you forgot. Today is for the math, not the dream. Knowing what you have is the first step.",
  },
  "4.7": {
    name: "Day of honoring commitments",
    tags: ["Reliability", "Trust", "Follow-through"],
    description:
      "Do what you said you'd do — even the small things, especially the small things. Today's reputation is built on follow-through. The promise kept today buys trust you'll need later.",
  },
  "4.8": {
    name: "Day of resisting shortcuts",
    tags: ["Integrity", "Patience", "Craft"],
    description:
      "The shortcut will look attractive. It always does. Today is the day to take the long way — to do it properly, completely, without the corners cut. The work that lasts is built this way.",
  },
  "4.9": {
    name: "Day of grounded body",
    tags: ["Body", "Grounding", "Care"],
    description:
      "The mind has been busy; the body needs you back. Eat actual food. Stretch. Walk somewhere. The discipline of caring for your physical self is the discipline that holds everything else together.",
  },
  "4.10": {
    name: "Day of building the system",
    tags: ["System", "Process", "Long-term"],
    description:
      "Today rewards thinking about the structure, not the individual task. What process will save you ten hours next month? What template, habit, or system? Build the engine, not the output.",
  },
  "5.1": {
    name: "Day of change and movement",
    tags: ["Change", "Freedom", "Adventure"],
    description:
      "The day brings restlessness — channel it. Travel, conversation, a new route home, an unexpected idea. Resist locking yourself into rigid plans. Stay flexible; the unexpected is where today's gold lies.",
  },
  "5.2": {
    name: "Day of saying yes",
    tags: ["Yes", "Adventure", "Openness"],
    description:
      "The unexpected invitation arrives today. The default answer should be yes. Even if it's small — the lunch, the detour, the new face. The energy of 5 expands what's in your life. Let it.",
  },
  "5.3": {
    name: "Day of breaking the routine",
    tags: ["Disruption", "Newness", "Freshness"],
    description:
      "Whatever you usually do today — don't. Take the other route, eat somewhere new, work from a different chair. Routine is the enemy today. A small change unlocks something larger.",
  },
  "5.4": {
    name: "Day of bodily adventure",
    tags: ["Body", "Sensation", "Aliveness"],
    description:
      "Today wants the senses awake. New food, new music, new touch, new movement. Sitting still today feels heavier than it should. Get up. Go somewhere. Feel something fresh.",
  },
  "5.5": {
    name: "Day of curious questions",
    tags: ["Curiosity", "Questions", "Exploration"],
    description:
      "Ask the question you usually skip. The taxi driver's story, the friend's actual feelings, the topic you avoid because it's complicated. Today's adventure might happen in a conversation.",
  },
  "5.6": {
    name: "Day of letting go of the plan",
    tags: ["Flexibility", "Openness", "Trust"],
    description:
      "The schedule will not survive today. That's fine. Lean into the disruption. Whatever derails you is probably the actual story of your day. Hold the plan loosely.",
  },
  "5.7": {
    name: "Day of new territory",
    tags: ["Exploration", "Newness", "Boldness"],
    description:
      "Go somewhere you've never been — even if it's two blocks away. Today's growth lives in unfamiliar physical space. Geography changes thought.",
  },
  "5.8": {
    name: "Day of conversation with strangers",
    tags: ["Connection", "Stranger", "Openness"],
    description:
      "The most interesting person today is someone you don't know yet. Open a conversation in line, at the gym, in the elevator. Today rewards the small social risk.",
  },
  "5.9": {
    name: "Day of quick movement",
    tags: ["Speed", "Action", "Decisiveness"],
    description:
      "Slow deliberation is the enemy today. Move quickly, decide quickly, adjust as you go. The momentum is more valuable than the plan. Trust that you'll course-correct in motion.",
  },
  "5.10": {
    name: "Day of freedom from old commitments",
    tags: ["Release", "Freedom", "Lightness"],
    description:
      "Something needs to be released today — not endings, just looser holds. The plan you can cancel. The promise you should renegotiate. The story about yourself that's outdated. Set it down.",
  },
  "6.1": {
    name: "Day of responsibility and care",
    tags: ["Family", "Balance", "Service"],
    description:
      "Today pulls you toward the people who depend on you. Show up. Tend to home, relationships, and the small acts that hold a life together. If you've been over-giving, find a way to receive too. Balance is the lesson.",
  },
  "6.2": {
    name: "Day of family attention",
    tags: ["Family", "Connection", "Presence"],
    description:
      "Call the person you've been meaning to call. Today's energy wants the family thread tended. A short conversation today repairs more than you expect.",
  },
  "6.3": {
    name: "Day of caring for home",
    tags: ["Home", "Comfort", "Order"],
    description:
      "The space around you affects everything else. Today is for the home: a clean kitchen, a made bed, a candle lit. Small acts of care for your space become acts of care for yourself.",
  },
  "6.4": {
    name: "Day of nurturing others",
    tags: ["Nurture", "Service", "Care"],
    description:
      "Someone needs you today and won't ask. Notice it. A meal, a check-in, a listening ear. The energy of 6 finds you through quiet acts of generosity.",
  },
  "6.5": {
    name: "Day of balancing give and take",
    tags: ["Balance", "Reciprocity", "Fairness"],
    description:
      "Look at your relationships honestly today. Where are you over-giving? Where are you over-taking? The work of 6 is the work of fair exchange. Make a small correction.",
  },
  "6.6": {
    name: "Day of beauty in the everyday",
    tags: ["Beauty", "Aesthetics", "Care"],
    description:
      "Take a moment to make something around you more beautiful. Flowers on the table, fresh sheets, the candle lit. Today, beauty is not luxury — it's how care becomes visible.",
  },
  "6.7": {
    name: "Day of healing a small rupture",
    tags: ["Repair", "Reconnection", "Care"],
    description:
      "The misunderstanding from last week — address it today. Lightly. The longer it sits, the heavier it gets. A short, warm acknowledgment can reset everything.",
  },
  "6.8": {
    name: "Day of saying no with love",
    tags: ["Boundary", "Self-care", "Service"],
    description:
      "The work of 6 includes knowing when to stop giving. Today, the kindest thing might be no. Said softly, said clearly. Saying no to one thing makes room for the right yes.",
  },
  "6.9": {
    name: "Day of community",
    tags: ["Community", "Belonging", "Service"],
    description:
      "Today is bigger than the household. Show up for the group — the team, the neighborhood, the friend's birthday. Belonging is a muscle. Today, use it.",
  },
  "6.10": {
    name: "Day of cooking and sharing",
    tags: ["Nourishment", "Sharing", "Comfort"],
    description:
      "Feed someone today, including yourself. Cook the actual meal. Sit down to eat. Invite someone if you can. The energy of 6 lives in shared tables.",
  },
  "7.1": {
    name: "Day of wisdom and solitude",
    tags: ["Insight", "Reflection", "Stillness"],
    description:
      "Today's energy invites reflection, analysis, and inner stillness. It's a good day to slow down, spend time alone, and truly listen to your own voice. Avoid rushing into big decisions — trust your intuition and observe what unfolds around you. The answers won't come from the outside, but from within.",
  },
  "7.2": {
    name: "Day of inner listening",
    tags: ["Intuition", "Stillness", "Inner voice"],
    description:
      "Quiet the input today. Less news, less scroll, less talking. The voice that has been trying to reach you needs silence to land. Sit with yourself for an hour without a screen. Listen.",
  },
  "7.3": {
    name: "Day of walking alone",
    tags: ["Solitude", "Walk", "Nature"],
    description:
      "Take a long walk today. Without headphones if you can. The body in motion, the mind unguarded — this is where 7's insights arrive. The walk is the work.",
  },
  "7.4": {
    name: "Day of reading deeply",
    tags: ["Study", "Depth", "Focus"],
    description:
      "Today rewards depth over breadth. Read one thing slowly. Watch one talk fully. Sit with one idea long enough for it to actually land. Surface skimming wastes today.",
  },
  "7.5": {
    name: "Day of trusting intuition",
    tags: ["Intuition", "Trust", "Knowing"],
    description:
      "The thing you sense but can't justify — that's the truth today. Don't ask others to validate it. Don't reason your way out. The energy of 7 trusts what the gut already knows.",
  },
  "7.6": {
    name: "Day of withdrawal",
    tags: ["Solitude", "Privacy", "Rest"],
    description:
      "Cancel what you can. Decline the invitation. Stay home. Today is for the quiet you've been postponing. Withdrawal is not avoidance — it's restoration.",
  },
  "7.7": {
    name: "Day of journaling what surfaces",
    tags: ["Writing", "Reflection", "Self-knowledge"],
    description:
      "Write today, even if you don't usually. A page, in the morning, of whatever comes up. The hidden pattern will show itself on paper before it shows itself in life.",
  },
  "7.8": {
    name: "Day of spiritual practice",
    tags: ["Spirit", "Practice", "Depth"],
    description:
      "Whatever your practice is — meditation, prayer, ritual, silence — return to it today. The energy of 7 amplifies what you do in stillness. Don't skip it.",
  },
  "7.9": {
    name: "Day of noticing patterns",
    tags: ["Pattern", "Insight", "Observation"],
    description:
      "Today, the same theme will surface in three places. Notice it. The universe (or your unconscious) is trying to point at something. The pattern is the message.",
  },
  "7.10": {
    name: "Day of slow decisions",
    tags: ["Patience", "Discernment", "Inner wisdom"],
    description:
      "Don't decide the big thing today. Sit with it. The first answer that comes is rarely the right one for 7's energy. Sleep on it. Sit with it. The clarity arrives, but on its own time.",
  },
  "8.1": {
    name: "Day of power and achievement",
    tags: ["Power", "Decision", "Abundance"],
    description:
      "Today favors big moves. Material matters — money, work, ambition — are sharper than usual. Make the call. Sign the thing. Step into your authority without apology. What you build today carries weight.",
  },
  "8.2": {
    name: "Day of asking for more",
    tags: ["Negotiation", "Worth", "Authority"],
    description:
      "Ask for the raise, the rate, the seat at the table. Today the answer is more likely yes than you fear. The energy of 8 backs the person who knows their worth and states it plainly.",
  },
  "8.3": {
    name: "Day of money in motion",
    tags: ["Money", "Decisions", "Wealth"],
    description:
      "Look at the financial picture today. Move the money — invest, pay off, transfer, save. Today rewards decisive action with resources. Sitting on the question is more expensive than acting.",
  },
  "8.4": {
    name: "Day of strategic thinking",
    tags: ["Strategy", "Long-term", "Power"],
    description:
      "Zoom out. Today is for the bigger picture, not the immediate task. Where is this all going? What's the move that compounds over the next year? Plan from the top.",
  },
  "8.5": {
    name: "Day of taking authority",
    tags: ["Authority", "Leadership", "Ownership"],
    description:
      "The seat is empty because no one has claimed it. Today's the day. Take responsibility for the project, the decision, the team. The energy of 8 trusts whoever steps up.",
  },
  "8.6": {
    name: "Day of business clarity",
    tags: ["Business", "Clarity", "Focus"],
    description:
      "The fog clears today on a work matter. You'll see exactly what to do. Move on it before the clarity fades. Today rewards execution following insight.",
  },
  "8.7": {
    name: "Day of building wealth",
    tags: ["Wealth", "Building", "Long-term"],
    description:
      "Today, take one action that builds toward your financial future — not just this month's bill. Investments, savings, skills, contracts. The energy of 8 lives in the long game.",
  },
  "8.8": {
    name: "Day of confronting the issue",
    tags: ["Confrontation", "Truth", "Power"],
    description:
      "The conversation you've been avoiding — have it today. The energy of 8 supports honest power. Said directly, with confidence, the issue can resolve. Avoided again, it festers.",
  },
  "8.9": {
    name: "Day of rewarding yourself",
    tags: ["Reward", "Pleasure", "Self-worth"],
    description:
      "You've earned something. Don't postpone the reward. The energy of 8 includes enjoyment of what you've built. Buy the thing, take the trip, sit down with the good wine.",
  },
  "8.10": {
    name: "Day of stepping into the lead",
    tags: ["Leadership", "Power", "Decision"],
    description:
      "Today, lead. The team is waiting for direction. The family wants someone to call it. The friend group needs a plan. The energy of 8 wants you visible, decisive, in front.",
  },
  "9.1": {
    name: "Day of completion and release",
    tags: ["Closure", "Release", "Compassion"],
    description:
      "Today is for finishing, not starting. Close the loop, send the final message, let go of what you've been holding. There's grief in endings, but also relief. Be kind — to yourself and to others. Tomorrow is a new cycle.",
  },
  "9.2": {
    name: "Day of letting go",
    tags: ["Release", "Surrender", "Lightness"],
    description:
      "Whatever you've been gripping — open your hand. The grudge, the regret, the plan that won't work, the relationship that's over. Today, the act of release is the gift.",
  },
  "9.3": {
    name: "Day of forgiveness",
    tags: ["Forgiveness", "Compassion", "Release"],
    description:
      "Someone, including possibly yourself, needs grace today. Not approval — grace. The act of forgiveness is for you, not for them. Today's energy supports the work of letting go cleanly.",
  },
  "9.4": {
    name: "Day of clearing space",
    tags: ["Decluttering", "Clearing", "Renewal"],
    description:
      "Clean out something today. Closet, files, contact list, calendar. The act of physical clearing creates emotional space. By evening, you'll feel lighter than you started.",
  },
  "9.5": {
    name: "Day of finishing the project",
    tags: ["Completion", "Finish", "Closure"],
    description:
      "The unfinished thing — close it today. Even if it's not perfect. The energy of 9 rewards completion over polish. Done is more powerful than ongoing.",
  },
  "9.6": {
    name: "Day of helping anonymously",
    tags: ["Service", "Compassion", "Generosity"],
    description:
      "Help someone today without taking credit. Quietly cover a tab, send the gift without a card, do the favor without mentioning it. Today's energy multiplies invisible generosity.",
  },
  "9.7": {
    name: "Day of saying goodbye",
    tags: ["Goodbye", "Honor", "Ending"],
    description:
      "Some endings deserve a real ceremony. Today is the day to honor what's ending — even if it's just internally. A walk, a journal entry, a short ritual. Give the chapter the closing it deserves.",
  },
  "9.8": {
    name: "Day of compassion for self",
    tags: ["Self-compassion", "Kindness", "Rest"],
    description:
      "You've been hard on yourself. Today, soften. Speak to yourself as you would to a friend who's tired. The energy of 9 includes turning compassion inward. You're allowed.",
  },
  "9.9": {
    name: "Day of sharing what you've learned",
    tags: ["Wisdom", "Sharing", "Teaching"],
    description:
      "What you've worked out the hard way — pass it on today. A conversation, a message, a written reflection. The energy of 9 honors the teacher in you. Someone needs what you know.",
  },
  "9.10": {
    name: "Day of bigger perspective",
    tags: ["Perspective", "Wisdom", "Acceptance"],
    description:
      "Step way back today. The thing that feels huge — it's not. In the bigger picture, it's a chapter. Today, the perspective shift is the medicine. Zoom out until what hurt feels human-sized.",
  },
}
